import { useMutation, useQuery } from '@tanstack/react-query';
import { ILegalClientRelationshipInput } from '../../Interfaces/ILegalClientRelationship/ILegalClientRelationshipInput';
import { customFetcher } from '../useFetch';
import { ILegalClientRelationship } from '../../Interfaces/ILegalClientRelationship/ILegalClientRelationship';
import { IRelationshipType } from '../../Interfaces/IRelationshipType';
import { IRelationshipRole } from '../../Interfaces/IRelationshipRole';
import { IRelationshipTypeHasRelationshipRole } from '../../Interfaces/IRelationshipTypeHasRelationshipRole';

//////////////////// Relationships
export const useGetRelationshipsByLegalClientId = (legalClientId: number) => {
  const {
    data: legalClientRelationships,
    isLoading: isLoadingLegalClientRelationships,
    refetch: refetchLegalClientRelationships,
  } = useQuery<ILegalClientRelationship[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalClientRelationships', legalClientId],
    queryFn: () =>
      customFetcher({
        endpoint: `/relationship/getByLegalClient/${legalClientId}`,
      }),
  });

  return {
    legalClientRelationships,
    isLoadingLegalClientRelationships,
    refetchLegalClientRelationships,
  };
};

export const useGetLegalClientRelationsByLegalClientId = (
  legalClientId: number
) => {
  const {
    data: legalClientRelations,
    isLoading: isLoadingLegalClientRelations,
    refetch: refetchLegalClientRelations,
  } = useQuery<ILegalClientRelationship[]>({
    refetchOnWindowFocus: false,
    queryKey: ['legalclientrelationship'],
    queryFn: () =>
      customFetcher({
        endpoint: `/relationship/getByLegalClient/${legalClientId}`,
      }),
  });

  return {
    legalClientRelations,
    isLoadingLegalClientRelations,
    refetchLegalClientRelations,
  };
};

export const useCreateRelationshipFullObject = () => {
  const {
    mutateAsync: createRelationshipFullObject,
    isPending: isPendingCreateRelationshipFullObject,
  } = useMutation({
    mutationFn: (input: ILegalClientRelationshipInput) =>
      customFetcher({
        endpoint: '/relationship/fullObject',
        method: 'POST',
        body: input,
      }),
  });
  return {
    createRelationshipFullObject,
    isPendingCreateRelationshipFullObject,
  };
};

export const useUpdateRelationshipFullObject = () => {
  const {
    mutateAsync: updateRelationshipFullObject,
    isPending: isPendingUpdateRelationshipFullObject,
  } = useMutation({
    mutationFn: (input: ILegalClientRelationshipInput) =>
      customFetcher({
        endpoint: `/relationship/fullObject`,
        method: 'PUT',
        body: input,
      }),
  });

  return {
    updateRelationshipFullObject,
    isPendingUpdateRelationshipFullObject,
  };
};

export const useDeleteRelationship = () => {
  const {
    mutateAsync: deleteRelationship,
    isPending: isPendingDeleteRelationship,
  } = useMutation({
    mutationFn: (relationshipId: number) =>
      customFetcher({
        endpoint: `/relationship/${relationshipId}`,
        method: 'DELETE',
      }),
  });

  return {
    deleteRelationship,
    isPendingDeleteRelationship,
  };
};

export const useGetRelationshipTypes = () => {
  const { data: relationshipTypes, isLoading: isLoadingRelationshipTypes } =
    useQuery<IRelationshipType[]>({
      queryKey: ['relationshiptype'],
      queryFn: () => customFetcher({ endpoint: '/relationshiptype' }),
    });

  return { relationshipTypes, isLoadingRelationshipTypes };
};

export const useGetRelationshipRoles = () => {
  const { data: relationshipRoles, isLoading: isLoadingRelationshipRoles } =
    useQuery<IRelationshipRole[]>({
      queryKey: ['relationshiprole'],
      queryFn: () => customFetcher({ endpoint: '/relationshiprole' }),
    });

  return { relationshipRoles, isLoadingRelationshipRoles };
};

export const useGetRelationshipTypeHasRelationshipRole = () => {
  const {
    data: relationshipTypeHasRelationshipRole,
    isLoading: isLoadingRelationshipTypeHasRelationshipRole,
  } = useQuery<IRelationshipTypeHasRelationshipRole[]>({
    queryKey: ['relationshiptype_has_relationshiprole'],
    queryFn: () =>
      customFetcher({ endpoint: '/relationshiptype_has_relationshiprole' }),
  });

  return {
    relationshipTypeHasRelationshipRole,
    isLoadingRelationshipTypeHasRelationshipRole,
  };
};
